import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { DateRangeField } from '@/components/dateRangeField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    PERIOD_SHORTCUTS,
    PERIOD_SHORTCUT_LABEL_KEY,
    type PeriodShortcut,
} from './periodControlMeta';

type PeriodControlViewProps = {
    /** ISO `YYYY-MM-DD`. */
    fromDate: string;
    toDate: string;
    selectedShortcut: PeriodShortcut;
    isPickerOpen: boolean;
    onPickerOpenChange: (open: boolean) => void;
    onShortcutChange: (key: React.Key) => void;
    onDateRangeChange: (fromDate: string, toDate: string) => void;
};

/**
 * Render-only sales-page date-range filter. Two halves:
 *
 *  - Left: a `Segment` of shortcut buttons (Today / This week / This
 *    month / This year / Custom). The selected key always mirrors the
 *    current date range — if the user nudges the picker off a preset
 *    boundary, `'custom'` takes over.
 *  - Right: a `DateRangePicker` showing the same range. Clicking
 *    `Custom` while another preset is active opens the picker.
 *
 * All range/URL/provider logic lives in {@link usePeriodControl}.
 */
export const PeriodControlView: React.FC<PeriodControlViewProps> = ({
    fromDate,
    toDate,
    selectedShortcut,
    isPickerOpen,
    onPickerOpenChange,
    onShortcutChange,
    onDateRangeChange,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            shortcutsLabel: t('sales.widgets.periodControl.shortcutsLabel'),
            rangeLabel: t('sales.widgets.periodControl.rangeLabel'),
        }),
        [t],
    );

    return (
        <section
            // Stick the filter bar flush below the AppLayout navbar (h=56px, z-40)
            // so it stays visible as the user scrolls the report. z-30 keeps it
            // above page Cards but below modals/sheets (z-50).
            aria-label={copy.shortcutsLabel}
            className="sticky top-14 z-30 bg-background py-3"
            {...getNodeTestIdProp('period-control-section')}
        >
            <Card
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('period-control-card')}
            >
                <Segment
                    aria-label={copy.shortcutsLabel}
                    onSelectionChange={onShortcutChange}
                    selectedKey={selectedShortcut}
                    size="sm"
                    variant="ghost"
                    {...getNodeTestIdProp('period-control-shortcuts')}
                >
                    {PERIOD_SHORTCUTS.map((shortcut) => (
                        <Segment.Item
                            id={shortcut}
                            key={shortcut}
                            {...getNodeTestIdProp(
                                `period-control-shortcut-${shortcut}`,
                            )}
                        >
                            <Segment.Separator />
                            {t(PERIOD_SHORTCUT_LABEL_KEY[shortcut])}
                        </Segment.Item>
                    ))}
                </Segment>
                <DateRangeField
                    ariaLabel={copy.rangeLabel}
                    fromDate={fromDate}
                    fullWidth
                    isOpen={isPickerOpen}
                    onChange={onDateRangeChange}
                    onOpenChange={onPickerOpenChange}
                    slot="period-control"
                    toDate={toDate}
                />
            </Card>
        </section>
    );
};
